import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSale, syncMidtrans, syncCashless, getSaleDetail,
  resumePayment,
} from '@/api/sale.api';
import { validateCoupon } from '@/api/coupon.api';
import { printBatch, printSingle } from '@/api/qrCode.api';
import { bindUserDevice, type BindDevicePayload } from '@/api/device.api';
import { useDeviceStore } from '@/stores/device.store';
import type { CreateSalePayload } from '@/types/sale';

export function useCreateSale() {
  return useMutation({
    mutationKey: ['sale', 'create'],
    mutationFn: (args: { payload: CreateSalePayload; idempotencyKey?: string }) =>
      createSale(args.payload, args.idempotencyKey),
  });
}

export function useValidateCoupon() {
  return useMutation({
    mutationKey: ['coupon', 'validate'],
    mutationFn: (code: string) => validateCoupon(code),
  });
}

export function usePrintBatch() {
  return useMutation({
    mutationKey: ['print', 'batch'],
    mutationFn: (invoiceId: string) => printBatch(invoiceId),
  });
}

export function useReprintSingle() {
  return useMutation({
    mutationKey: ['print', 'single'],
    mutationFn: (qrId: string) => printSingle(qrId),
  });
}

export function useSaleDetail() {
  return useMutation({
    mutationKey: ['sale', 'detail'],
    mutationFn: (invoiceId: string) => getSaleDetail(invoiceId),
  });
}

/**
 * Re-open an unpaid sale. The server syncs with the gateway first, so this can
 * come back already SUCCESS for a payment whose webhook never arrived.
 */
export function useResumePayment() {
  return useMutation({
    mutationKey: ['sale', 'resume'],
    mutationFn: (invoiceId: string) => resumePayment(invoiceId),
  });
}

export function useSyncMidtrans() {
  return useMutation({
    mutationKey: ['sync', 'midtrans'],
    mutationFn: (invoiceId: string) => syncMidtrans(invoiceId),
  });
}

export function useSyncCashless() {
  return useMutation({
    mutationKey: ['sync', 'cashless'],
    mutationFn: (invoiceId: string) => syncCashless(invoiceId),
  });
}

export function useBindDevice() {
  const qc = useQueryClient();
  const hydrate = useDeviceStore((s) => s.hydrateFromBinding);
  return useMutation({
    mutationKey: ['device', 'bind'],
    mutationFn: (payload: BindDevicePayload) => bindUserDevice(payload),
    onSuccess: (binding) => {
      hydrate(binding);
      qc.invalidateQueries({ queryKey: ['deviceBinding'] });
    },
  });
}
